
(function () {

    'use strict';

    // NOTE ensure the namespace and any dependencies are set up appropriately, for both server and client
    if (typeof window === 'undefined') {
        (function () {
            const _ = require('lodash');
            const eventDisplay = require('../../../public/modules/utils/eventDisplay.js');
            const companyEvents = require('../../../public/modules/utils/companyEvents.js');
            const formatter = require('../../../public/modules/formatter.js');
            const performanceSegmentType = require('../../../public/apps/alerts/enums/performanceSegmentTypes.js');
            const linkUtils = require('../../../public/apps/adsInsights/linkUtils.js');

            module.exports = definition(eventDisplay, companyEvents, formatter, performanceSegmentType, linkUtils, _);
        })();
    } else {
        (function () {
            RivalIQ.apps.Alerts.module('utils');

            const eventDisplay = RivalIQ.utils.eventDisplay;
            const companyEvents = RivalIQ.utils.companyEvents;
            const formatter = RivalIQ.formatter;
            const performanceSegmentType = RivalIQ.apps.Alerts.enums.performanceSegmentType;
            const linkUtils = RivalIQ.apps.AdsInsights.utils;

            const utils = definition(eventDisplay, companyEvents, formatter, performanceSegmentType, linkUtils, window._);

            window._.extend(RivalIQ.apps.Alerts.utils, utils);
        })();
    }

    function definition(eventDisplay, companyEvents, formatter, performanceSegmentType, linkUtils, _) {

        function formatCurrency(value) {
            return formatter.formatAmountAndCurrency(value);
        }

        function getAdObject(data) {
            return _.get(data, ['data', 'adObject', 'adObject']);
        }

        function getCampaignAdObject(data) {
            return _.get(data, ['data', 'campaign', 'adObject']);
        }

        function getCampaignName(data) {

            const adObject = _.get(data, ['data', 'campaign', 'adObject'], getAdObject(data));
            return _.get(adObject, ['campaign_name'], 'n/a').trim();
        }

        function getCampaignUrl(data) {

            const adObject = getAdObject(data);
            const adAccountId = _.get(adObject, ['adAccountId']);
            const adObjectId = _.get(adObject, ['campaign_id']);
            const adObjectType = _.get(adObject, ['adObjectType']);

            return linkUtils.adObjectAppPath(adAccountId, adObjectId, adObjectType);
        }

        function getAlertTitle(data) {

            return eventDisplay.getTitle(data.event_type_id, data);
        }

        function getAlertCategory(data) {
            const eventDetails = eventDisplay.eventDetails[data.event_type_id];
            const category = eventDisplay.eventCategoryDetails[eventDetails.category];
            return (category) ? category.name : '';
        }

        function getAlertIconClassName(data) {
            const eventTypeId = data.event_type_id;

            const sourceDetails = eventDisplay.getSourceDetails(eventTypeId);
            return sourceDetails.fontIcon;
        }

        function getAlertIconBadge(data) {
            const eventTypeId = data.event_type_id;

            const sourceDetails = eventDisplay.getSourceDetails(eventTypeId);
            return sourceDetails.fontIconBadge;
        }

        function getPerformanceDescription(data, withMarkup) {

            const adObject = getAdObject(data);
            const campaignAdObject = getCampaignAdObject(data);
            const campaignName = getCampaignName(data);
            const segmentDescription = getSegmentDescriptionName(data);
            const segmentName = getSegmentOrAdSetName(data);
            const costPerResult = _.get(adObject, ['cost_per_result', 'value']);
            const averageCostPerResult = _.get(campaignAdObject, ['cost_per_result', 'value']);

            let description = 'For your campaign, ';

            description += (withMarkup ? '<i><span class="font-semibold">' : '');
            description += campaignName;
            description += (withMarkup ? '</span></i>' : '');

            description += `, the ${segmentDescription} `;

            description += (withMarkup ? '<strong>' : '');

            description += (`${segmentName} has a `);
            if (costPerResult > averageCostPerResult) {
                description += (`${Math.floor(((costPerResult / averageCostPerResult) - 1) * 100)}% higher Cost per Result`);
            } else {
                description += (`${Math.floor((1 - (costPerResult / averageCostPerResult)) * 100)}% lower Cost per Result`);
            }
            description += (withMarkup ? '</strong>' : '');
            description += (` than the average of ${formatCurrency(_.get(campaignAdObject, ['cost_per_result']))}.`);

            return description;
        }

        function getAlertDescription(data, withMarkup) {
            const eventTypeId = data.event_type_id;
            const eventType = companyEvents.eventType;

            switch (eventTypeId) {
                case eventType.facebookAdsUnderPerforming:
                case eventType.facebookAdsTopPerformer:
                    return getPerformanceDescription(data, withMarkup);
                default:
                    return 'Unknown event type :(';
            }
        }

        function getIconUrl(data, urlBasepath) {
            const url = data.icon_url;

            // not all accounts have a primary page picture, fall back is the facebook icon
            const eventTypeId = data.event_type_id;
            const sourceDetails = eventDisplay.getSourceDetails(eventTypeId);

            return url || urlBasepath + sourceDetails.iconUrl;
        }

        function getCampaignSpend(data) {

            const adObject = getCampaignAdObject(data);
            return _.get(adObject, ['spend']);
        }

        function getCampaignResults(data) {

            const adObject = getCampaignAdObject(data);
            return _.get(adObject, ['results', 'value']);
        }

        function getSegmentTypeName(data) {
            const segmentType = _.get(data, ['data', 'segmentType']);

            return {
                adset: 'Ad Set',
                age_gender: 'Age/Gender',
                placement: 'Placement'
            }[segmentType] || 'Ad Set';
        }

        function getSegmentDescriptionName(data) {
            const segmentType = _.get(data, ['data', 'segmentType']);

            return {
                adset: 'ad set',
                age_gender: 'segment',
                placement: 'placement'
            }[segmentType] || 'ad set';
        }

        function getSegmentOrAdSetName(data) {
            const segmentType = _.get(data, ['data', 'segmentType']);

            const adObject = getAdObject(data);

            if (segmentType === performanceSegmentType.ADSET) {
                return _.get(adObject, ['adset_name']);
            } else {
                return _.get(adObject, ['name']);
            }
        }

        function getCampaignCostPerResult(data) {
            const adObject = getCampaignAdObject(data);
            return _.get(adObject, ['cost_per_result']);
        }

        function getLinkObject(data) {
            let adObject = getAdObject(data);

            // if this is a breakdown we go to the campaign
            if (adObject.isBreakdown) {
                adObject = getCampaignAdObject(data);
            } else {
                adObject = linkUtils.constructParentAdObject(adObject);
            }

            return adObject;
        }

        function getBreadCrumbs(data) {
            const adObject = getLinkObject(data);
            const adAccountId = _.get(adObject, ['adAccountId']);
            const adObjectId = _.get(data, ['data', 'adObject', 'boundDimensions', 'adObjectId']);

            return linkUtils.getBreadCrumbsForAdObject(adAccountId, adObjectId, adObject);
        }

        function getEditLink(data) {

            const adObject = getLinkObject(data);

            return linkUtils.constructNativeLink(adObject);
        }

        function getAccountName(data) {
            const adObject = getCampaignAdObject(data) || getAdObject(data);
            return _.get(adObject, ['account_name'], '').trim();
        }

        return {
            getAlertDescription: getAlertDescription,
            getAccountName: getAccountName,
            getFacebookAdsTemplateData: function (data, urlBasepath) {

                const adObject = getAdObject(data);
                const campaignName = getCampaignName(data);
                const campaignUrl = getCampaignUrl(data);
                const eventTitle = getAlertTitle(data);

                _.extend(data, adObject, {
                    eventTitle: eventTitle,
                    eventCategory: getAlertCategory(data),
                    alertIconClassName: getAlertIconClassName(data),
                    alertIconBadge: getAlertIconBadge(data),
                    alertDescription: getAlertDescription(data, true),
                    companyName: eventTitle,
                    companyUrl: campaignUrl,
                    companyIconUrl: getIconUrl(data, urlBasepath),
                    companyAbbr: campaignName,
                    campaignName: campaignName,
                    spend: adObject.spend,
                    campaignSpend: getCampaignSpend(data),
                    campaignResults: getCampaignResults(data),
                    segmentTypeName: getSegmentTypeName(data),
                    segmentName: getSegmentOrAdSetName(data),
                    campaignCostPerResult: getCampaignCostPerResult(data),
                    formatCurrency: formatCurrency,
                    breadCrumbs: getBreadCrumbs(data),
                    editLink: getEditLink(data),
                    editText: 'Edit in Facebook'
                });

            }
        };

    }
})();
